import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Chip,
  Grid,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import CloudIcon from "@mui/icons-material/Cloud";
import StorageIcon from "@mui/icons-material/Storage";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";

const BUNNY_REGIONS = [
  { value: "de", label: "Frankfurt (DE)" },
  { value: "ny", label: "New York (NY)" },
  { value: "la", label: "Los Angeles (LA)" },
  { value: "sg", label: "Singapore (SG)" },
  { value: "syd", label: "Sydney (SYD)" },
  { value: "uk", label: "London (UK)" },
  { value: "se", label: "Stockholm (SE)" },
  { value: "br", label: "São Paulo (BR)" },
];

const AWS_REGIONS = [
  "us-east-1",
  "us-east-2",
  "us-west-1",
  "us-west-2",
  "eu-west-1",
  "eu-west-2",
  "eu-central-1",
  "ap-southeast-1",
  "ap-southeast-2",
  "ap-northeast-1",
  "ca-central-1",
  "sa-east-1",
];

const StorageSettings = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const [providers, setProviders] = useState([]);
  const [saving, setSaving] = useState({});
  const [stats, setStats] = useState([]);

  useEffect(() => {
    loadProviders();
    loadStats();
  }, []);

  const loadProviders = async () => {
    const res = await hitAxios({
      path: "/api/storage/get-all",
      admin: true,
      showSnackbar: false,
    });
    if (res?.data?.success) setProviders(res.data.data);
  };

  const loadStats = async () => {
    const res = await hitAxios({
      path: "/api/storage/stats",
      admin: true,
      showSnackbar: false,
    });
    if (res?.data?.success) setStats(res.data.data);
  };

  const updateProvider = (provider, field, value) => {
    setProviders((prev) =>
      prev.map((p) => (p.provider === provider ? { ...p, [field]: value } : p)),
    );
  };

  const updateConfig = (provider, key, value) => {
    setProviders((prev) =>
      prev.map((p) =>
        p.provider === provider
          ? { ...p, config: { ...p.config, [key]: value } }
          : p,
      ),
    );
  };

  const saveProvider = async (provider) => {
    const p = providers.find((x) => x.provider === provider);
    if (!p) return;
    setSaving((s) => ({ ...s, [provider]: true }));
    await hitAxios({
      path: "/api/storage/update",
      post: true,
      admin: true,
      obj: { provider: p.provider, config: p.config, is_active: p.is_active },
    });
    setSaving((s) => ({ ...s, [provider]: false }));
    loadProviders();
  };

  const setDefault = async (provider) => {
    await hitAxios({
      path: "/api/storage/set-default",
      post: true,
      admin: true,
      obj: { provider },
    });
    loadProviders();
  };

  const getStatCount = (provider, status) => {
    const row = stats.find(
      (s) => s.storage_provider === provider && s.upload_status === status,
    );
    return row?.count || 0;
  };

  const providerMeta = {
    local: {
      label: lang.localStorage || "Local Storage",
      icon: <StorageIcon />,
      color: "#6366F1",
      description:
        lang.localStorageDesc ||
        "Files saved on the server disk. No CDN delivery.",
    },
    bunnycdn: {
      label: "BunnyCDN",
      icon: <CloudIcon />,
      color: "#FF6B35",
      description:
        lang.bunnyCDNDesc || "Ultra-fast global CDN. Cheapest per-GB pricing.",
    },
    awss3: {
      label: "AWS S3",
      icon: <CloudIcon />,
      color: "#FF9900",
      description:
        lang.awsS3Desc || "Amazon S3 object storage with global availability.",
    },
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={1}>
        {lang.storageSettings || "Storage Settings"}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {lang.storageSettingsDesc ||
          "Configure where completed torrent files are stored and served from. The"}{" "}
        <strong>{lang.defaultProvider || "default provider"}</strong>{" "}
        {lang.storageSettingsDesc2 || "receives new uploads automatically."}
      </Typography>

      {/* Stats Summary */}
      {stats.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={600} mb={2}>
              {lang.storageStats || "Storage Stats"}
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              {["local", "bunnycdn", "awss3"].map((p) => (
                <Box key={p} sx={{ minWidth: 120 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {providerMeta[p]?.label}
                  </Typography>
                  <Stack direction="row" spacing={1} mt={0.5}>
                    <Chip
                      label={`${getStatCount(p, "uploaded")} ${lang.uploaded || "uploaded"}`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                    <Chip
                      label={`${getStatCount(p, "none")} ${lang.local || "local"}`}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Stack spacing={3}>
        {providers.map((p) => {
          const meta = providerMeta[p.provider];
          const isDefault = p.is_default === 1;

          return (
            <Card
              key={p.provider}
              sx={{
                border: isDefault ? "2px solid" : "1px solid",
                borderColor: isDefault ? meta.color : "divider",
              }}
            >
              <CardContent>
                {/* Header */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ color: meta.color }}>{meta.icon}</Box>
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h6" fontWeight={700}>
                          {meta.label}
                        </Typography>
                        {isDefault && (
                          <Chip
                            label={lang.default || "Default"}
                            size="small"
                            color="primary"
                            icon={<CheckCircleIcon />}
                          />
                        )}
                        {p.is_active === 1 && !isDefault && (
                          <Chip
                            label={lang.active || "Active"}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {meta.description}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    {!isDefault && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setDefault(p.provider)}
                      >
                        {lang.setAsDefault || "Set as Default"}
                      </Button>
                    )}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={p.is_active === 1}
                          onChange={(e) =>
                            updateProvider(
                              p.provider,
                              "is_active",
                              e.target.checked ? 1 : 0,
                            )
                          }
                          disabled={isDefault}
                        />
                      }
                      label={lang.active || "Active"}
                    />
                  </Stack>
                </Stack>

                <Divider sx={{ mb: 2 }} />

                {/* Local — no config needed */}
                {p.provider === "local" && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {lang.localStorageInfo ||
                      "Local storage requires no configuration. Files are saved to"}{" "}
                    <code>./downloads/</code>{" "}
                    {lang.onTheServer || "on the server."}
                  </Alert>
                )}

                {/* BunnyCDN Config */}
                {p.provider === "bunnycdn" && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={lang.storageZoneName || "Storage Zone Name"}
                        value={p.config?.storage_zone || ""}
                        onChange={(e) =>
                          updateConfig(
                            "bunnycdn",
                            "storage_zone",
                            e.target.value,
                          )
                        }
                        helperText={
                          lang.storageZoneHelper ||
                          "Your BunnyCDN storage zone name"
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="password"
                        label={
                          lang.storageAccessKey ||
                          "Storage Access Key (FTP Password)"
                        }
                        value={p.config?.access_key || ""}
                        onChange={(e) =>
                          updateConfig("bunnycdn", "access_key", e.target.value)
                        }
                        helperText={
                          lang.storageAccessKeyHelper ||
                          "Found in Storage Zone → FTP & API Access"
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={lang.pullZoneUrl || "Pull Zone URL"}
                        value={p.config?.pull_zone_url || ""}
                        onChange={(e) =>
                          updateConfig(
                            "bunnycdn",
                            "pull_zone_url",
                            e.target.value,
                          )
                        }
                        helperText={
                          lang.pullZoneHelper || "e.g. https://myzone.b-cdn.net"
                        }
                        placeholder="https://myzone.b-cdn.net"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>
                          {lang.storageRegion || "Storage Region"}
                        </InputLabel>
                        <Select
                          value={p.config?.region || "de"}
                          onChange={(e) =>
                            updateConfig("bunnycdn", "region", e.target.value)
                          }
                          label={lang.storageRegion || "Storage Region"}
                        >
                          {BUNNY_REGIONS.map((r) => (
                            <MenuItem key={r.value} value={r.value}>
                              {r.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                )}

                {/* AWS S3 Config */}
                {p.provider === "awss3" && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={lang.s3BucketName || "S3 Bucket Name"}
                        value={p.config?.bucket || ""}
                        onChange={(e) =>
                          updateConfig("awss3", "bucket", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>
                          {lang.awsRegion || "AWS Region"}
                        </InputLabel>
                        <Select
                          value={p.config?.region || "us-east-1"}
                          onChange={(e) =>
                            updateConfig("awss3", "region", e.target.value)
                          }
                          label={lang.awsRegion || "AWS Region"}
                        >
                          {AWS_REGIONS.map((r) => (
                            <MenuItem key={r} value={r}>
                              {r}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label={lang.accessKeyId || "Access Key ID"}
                        value={p.config?.access_key_id || ""}
                        onChange={(e) =>
                          updateConfig("awss3", "access_key_id", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="password"
                        label={lang.secretAccessKey || "Secret Access Key"}
                        value={p.config?.secret_access_key || ""}
                        onChange={(e) =>
                          updateConfig(
                            "awss3",
                            "secret_access_key",
                            e.target.value,
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label={
                          lang.publicBaseUrl || "Public Base URL (optional)"
                        }
                        value={p.config?.public_base_url || ""}
                        onChange={(e) =>
                          updateConfig(
                            "awss3",
                            "public_base_url",
                            e.target.value,
                          )
                        }
                        helperText={
                          lang.publicBaseUrlHelper ||
                          "If using CloudFront or custom domain, e.g. https://cdn.example.com. Leave blank to use default S3 URL."
                        }
                        placeholder="https://cdn.example.com"
                      />
                    </Grid>
                  </Grid>
                )}

                <Stack direction="row" spacing={2} mt={2}>
                  <Button
                    variant="contained"
                    onClick={() => saveProvider(p.provider)}
                    disabled={saving[p.provider]}
                    startIcon={
                      saving[p.provider] ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : null
                    }
                  >
                    {saving[p.provider]
                      ? lang.saving || "Saving..."
                      : lang.save || "Save"}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
};

export default StorageSettings;
